<template>
    <v-container fluid class="pa-0">
        <filter-panel
            v-if="showFiltersPanel"
            :filterOptions="filterOptions"
            :model-value="filterState"
            @update:modelValue="onUpdateFilters"
            class="mb-1"
        ></filter-panel>
        <v-data-table
            density="compact"
            :headers="dataCollectionTaskHeaders"
            :items="filteredDataCollectionTaskItems"
            no-results-text="No tasks."
            item-key="id"
            :items-per-page="25"
            :multi-select="showTemplateDownload"
            :show-select="showTemplateDownload"
            :value="selectedItems"
            :custom-key-sort="taskSortFns"
            fixed-header
            :height="tableHeight"
            v-on:update:model-value="updateSelection"
        >
            <template v-slot:item.filter="{ item }">
                {{ renderTaskFilter(item) }}
            </template>
            <template v-slot:item.taskStatusDescription="{ item }">
                <TaskStatusIcon :task="item" @openTaskStatusDialog="openTaskStatusDialog" />
            </template>
            <template v-slot:item.status="{ item }">
                <DataQCStatusChip :status="item.status" />
            </template>
            <template v-slot:item.type="{ item }">
                {{ item.type }}{{ item.internal ? ` internal` : `` }}
            </template>
            <template v-slot:item.editQC="{ item }">
                <v-icon @click="showQCDetailDialog(item)">$file-eye</v-icon>
            </template>
            <template v-slot:item.editFilter="{ item }">
                <v-icon @click="showEditDataCollectionTaskFilter(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.edit="{ item }">
                <v-icon @click="showEditDataCollectionTask(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon @click="deleteDataCollectionTask(item)">$delete</v-icon>
            </template>
            <template v-slot:item.userMeasurements="{ item }">
                <v-icon @click="showUserMeasurements(item)">$account-clock</v-icon>
            </template>
            <template v-slot:item.summaryMeasurements="{ item }">
                <v-icon @click="showSummaryMeasurements(item)">$sigma</v-icon>
            </template>
        </v-data-table>
    </v-container>
</template>
<script>
import { defineComponent } from "vue";

import {
    findTaskGroupById,
    findTaskDayInTaskGroups,
    getDateForTaskInTaskGroups,
    buildTaskFilterDisplay,
    buildTaskStatusDescription,
    getAdHocTaskDate,
} from "@/lib/taskUtils";
import { getISODateOnlyStringInSiteTimeZone } from "@/lib/dateUtils";
import DataQCStatusChip from "../DataQC/DataQCStatusChip.vue";
import { nullSafeSortDates, nullSafeSortStrings } from "@/lib/arrayUtils";
import TaskStatusIcon from "./TaskStatusIcon.vue";
import FilterPanel from "@/components/common/FilterPanel.vue";
import { useWindowHeight } from "@/lib/ts/useWindowHeight";
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";

export default defineComponent({
    components: { DataQCStatusChip, FilterPanel, TaskStatusIcon },

    setup() {
        const { windowHeight } = useWindowHeight();
        return { windowHeight };
    },

    props: {
        dataCollectionTasks: {
            type: Array,
            required: true,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
        protocol: {
            type: Object, // AbstractSchemaConfig
            required: true,
        },
        showEditButtons: {
            type: Boolean,
            required: true,
        },
        showMeasurementsButtons: {
            type: Boolean,
            required: true,
        },
        qcStatuses: {
            type: Array,
            required: false,
        },
        actualUserDates: {
            type: Array,
            required: false,
        },
        showTemplateDownload: {
            type: Boolean,
            required: true,
        },
        showFiltersPanel: {
            type: Boolean,
            default: true,
        },
        value: {
            // v-model binding
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            selectedDataCollectionTask: null,
            showDataCollectionTaskForm: false,
            taskSortFns: {
                taskStatusDescription: (a, b) => nullSafeSortStrings(a, b),
            },
            dataCollectionTaskHeaders: [
                {
                    title: "Task Group",
                    value: "taskGroupName",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Readout Group",
                    value: "readoutGroupName",
                    sortable: true,
                    align: "left",
                    width: "12%",
                },
                {
                    title: "Filter",
                    value: "filter",
                    sortable: false,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Anticipated Day",
                    value: "day",
                    align: "left",
                    sortable: true,
                    width: "2%",
                },
                {
                    title: "Collection Day",
                    value: "userDay",
                    align: "left",
                    sortable: true,
                    width: "2%",
                },
                {
                    title: "Anticipated Date",
                    value: "date",
                    align: "left",
                    sortable: true,
                    width: "7%",
                },
                {
                    title: "Collection Date Start",
                    value: "userStartDate",
                    align: "left",
                    sortable: true,
                    width: "7%",
                },
                {
                    title: "Collection Date Stop",
                    value: "userEndDate",
                    align: "left",
                    sortable: true,
                    width: "7%",
                },
                {
                    title: "Type",
                    value: "type",
                    sortable: false,
                    align: "left",
                    width: "8%",
                },
            ],
            selectedItems: [],
            filterState: {},
            filterOptions: {},
            filterConfig: [
                { name: "taskGroup", prop: "taskGroupName" },
                { name: "readoutGroup", prop: "readoutGroupName" },
                { name: "anticipatedDay", prop: "day" },
                { name: "collectionDay", prop: "userDay", noneOption: true },
                { name: "taskStatus", prop: "fieldStatus", noneOption: true },
                { name: "QCStatus", prop: "status" },
            ],
            dataCollectionTaskItems: [],
        };
    },

    computed: {
        tableHeight() {
            if (this.showFiltersPanel) {
                return this.windowHeight - 400;
            }
            return this.windowHeight - 300;
        },
        filteredDataCollectionTaskItems() {
            if (this.showFiltersPanel) {
                return filterData(
                    this.dataCollectionTaskItems,
                    this.filterState,
                    this.filterConfig
                );
            } else {
                return this.dataCollectionTaskItems;
            }
        },
    },

    mounted() {
        if (this.showEditButtons) {
            this.dataCollectionTaskHeaders.push({
                title: "Edit Filter",
                value: "editFilter",
                sortable: false,
                align: "left",
                width: "5%",
            });

            this.dataCollectionTaskHeaders.push({
                title: "Edit",
                value: "edit",
                sortable: false,
                align: "left",
                width: "5%",
            });

            this.dataCollectionTaskHeaders.push({
                title: "Delete",
                value: "delete",
                sortable: false,
                align: "left",
                width: "5%",
            });
        }
        if (this.showMeasurementsButtons) {
            this.dataCollectionTaskHeaders.push({
                title: "User Measurements",
                value: "userMeasurements",
                align: "left",
                sortable: false,
                width: "5%",
            });
            this.dataCollectionTaskHeaders.push({
                title: "Summary Measurements",
                value: "summaryMeasurements",
                align: "left",
                sortable: false,
                width: "5%",
            });
            this.dataCollectionTaskHeaders.push({
                title: "Task Status",
                value: "taskStatusDescription",
                align: "left",
                sortable: true,
                width: "1%",
            });
        }
        if (this.qcStatuses) {
            this.dataCollectionTaskHeaders.push(
                ...[
                    {
                        title: "QC Status",
                        value: "status",
                        sortable: true,
                        align: "left",
                        width: "5%",
                    },
                    {
                        title: "QC Notes",
                        value: "notes",
                        sortable: false,
                        align: "left",
                        width: "10%",
                    },
                    {
                        title: "Investigator Comments",
                        value: "investigatorComments",
                        sortable: false,
                        align: "left",
                        width: "10%",
                    },
                    {
                        title: "Edit QC Details",
                        value: "editQC",
                        sortable: false,
                        align: "left",
                        width: "5%",
                    },
                ]
            );
        }
    },

    methods: {
        mapDataCollectionTaskItems() {
            const items = this.dataCollectionTasks.map((dataCollectionTask) => {
                const baseItem = {
                    // copy the fields to the dataCollectionTask so the table works without a bunch of templates
                    ...dataCollectionTask,
                    // but keep a reference to the original task so we don't pass the whole dataCollectionTask object to other
                    // components
                    dataCollectionTask: dataCollectionTask,
                    readoutGroupName: this.getReadoutGroupNameById(
                        dataCollectionTask.readoutGroupId
                    ),
                };

                if (this.qcStatuses) {
                    const qcStatus = this.qcStatuses.find(
                        (qcItem) => qcItem.dataCollectionTaskId === dataCollectionTask.id
                    );
                    if (qcStatus) {
                        baseItem.status = qcStatus.status || "New";
                        baseItem.notes = qcStatus.comments;
                        baseItem.investigatorComments = qcStatus.investigatorComments;
                    }
                }

                if (dataCollectionTask.type === "ad-hoc") {
                    return {
                        ...baseItem,
                        taskGroupName: "",
                        day: "",
                        date: getAdHocTaskDate(dataCollectionTask),
                    };
                } else {
                    const userDay = this.findUserDayByDataCollectionTaskId(dataCollectionTask.id);
                    const userStartDate = this.findUserDateFieldByDataCollectionTaskId(
                        dataCollectionTask.id,
                        "userStartDate"
                    );
                    const userEndDate = this.findUserDateFieldByDataCollectionTaskId(
                        dataCollectionTask.id,
                        "userEndDate"
                    );

                    const taskStatusDescription = buildTaskStatusDescription(dataCollectionTask);
                    return {
                        ...baseItem,
                        taskGroupName: this.getTaskGroupNameById(dataCollectionTask.taskGroupId),
                        day: this.getTaskDay(dataCollectionTask),
                        date: this.getTaskDate(dataCollectionTask),
                        taskStatusDescription,
                        userDay,
                        userStartDate,
                        userEndDate,
                    };
                }
            });

            items.sort((a, b) => {
                return nullSafeSortDates(a.date, b.date);
            });
            this.dataCollectionTaskItems = items;
        },
        renderTaskFilter(item) {
            const readoutGroup = this.findReadoutGroupById(item.dataCollectionTask.readoutGroupId);
            return buildTaskFilterDisplay(
                item.dataCollectionTask,
                readoutGroup,
                this.experiment,
                this.protocol
            );
        },
        getTaskDay(dataCollectionTask) {
            return findTaskDayInTaskGroups(
                dataCollectionTask,
                this.taskGroups,
                this.experiment.day0
            );
        },
        getAdHocTaskDate(dataCollectionTask) {
            return getAdHocTaskDate(dataCollectionTask, this.experiment.siteTimeZone);
        },
        getTaskDate(dataCollectionTask) {
            return getDateForTaskInTaskGroups(
                dataCollectionTask,
                this.taskGroups,
                this.experiment.day0
            );
        },
        findTaskGroupById(taskGroupId) {
            return findTaskGroupById(taskGroupId, this.taskGroups);
        },
        getTaskGroupNameById(taskGroupId) {
            const taskGroup = this.findTaskGroupById(taskGroupId);
            return taskGroup.displayName;
        },
        getReadoutGroupNameById(readoutGroupId) {
            const readoutGroup = this.findReadoutGroupById(readoutGroupId);
            if (!readoutGroup) {
                return "";
            }
            return readoutGroup.name;
        },
        findReadoutGroupById(readoutGroupId) {
            return this.readoutGroups.find((readoutGroup) => readoutGroup.id === readoutGroupId);
        },
        showEditDataCollectionTaskFilter(item) {
            this.$emit("showEditDataCollectionTaskFilter", item.dataCollectionTask);
        },
        showEditDataCollectionTask(item) {
            this.$emit("showEditDataCollectionTask", item.dataCollectionTask);
        },
        showQCDetailDialog(item) {
            const qcItem = this.qcStatuses.find(
                (qcItem) => qcItem.dataCollectionTaskId === item.id
            );
            this.$emit("showQCDetailDialog", qcItem, item);
        },
        findUserDayByDataCollectionTaskId(dataCollectionTaskId) {
            if (!this.actualUserDates) {
                return "";
            }

            const userDate = this.actualUserDates.find(
                (userDateItem) => userDateItem.dataCollectionTaskId === dataCollectionTaskId
            );
            if (!userDate) {
                return "";
            }
            return userDate.userDay;
        },
        findUserDateFieldByDataCollectionTaskId(dataCollectionTaskId, field) {
            if (!this.actualUserDates) {
                return "";
            }

            const userDate = this.actualUserDates.find(
                (userDateItem) => userDateItem.dataCollectionTaskId === dataCollectionTaskId
            );
            if (!userDate) {
                return "";
            }
            return getISODateOnlyStringInSiteTimeZone(
                userDate[field],
                this.experiment.siteTimeZone
            );
        },
        deleteDataCollectionTask(item) {
            this.$emit("deleteDataCollectionTask", item.dataCollectionTask);
        },
        showUserMeasurements(item) {
            this.$emit("showUserMeasurements", {
                ...item.dataCollectionTask,
                anticipatedDate: item.date,
                collectionDateStart: item.userStartDate,
                collectionDateStop: item.userEndDate,
            });
        },
        showSummaryMeasurements(item) {
            this.$emit("showSummaryMeasurements", item.dataCollectionTask);
        },
        updateSelection(newSelectedItems) {
            this.$emit("updateSelectedItems", newSelectedItems);
        },
        openTaskStatusDialog(item) {
            this.$emit("openTaskStatusDialog", item.dataCollectionTask);
        },
        onUpdateFilters(newFilters) {
            this.filterState = newFilters;
        },
    },

    watch: {
        dataCollectionTaskItems: {
            immediate: true,
            handler() {
                this.filterOptions = buildFilterOptionsFromData(
                    this.dataCollectionTaskItems,
                    this.filterConfig
                );
            },
        },
        dataCollectionTasks: {
            immediate: true,
            handler() {
                this.mapDataCollectionTaskItems();
            },
        },
    },
});
</script>
<style></style>
